/**
 * @feature quote-requests
 * @layer presentation/components
 *
 * AdditionalConditionsSection - 추가 조건
 * Displays additional conditions (product) or requests (construction/manufacturing) as chips
 */

import { type FC } from "react";
import { CheckSquare } from "@semo/icons";
import { cn } from "../../../utils/cn";
import type { QuoteReviewSectionProps, LabelValuePair } from "../types";

/**
 * Additional Conditions Section Component
 * Shows conditions/requests as chips with primary background tint
 */
export const AdditionalConditionsSection: FC<QuoteReviewSectionProps> = ({
  categoryType,
  data,
  onEdit,
}) => {
  // Extract conditions and requests
  const isLabelValuePair = (item: unknown): item is LabelValuePair =>
    typeof item === "object" &&
    item !== null &&
    "label" in item &&
    "value" in item;

  const additionalConditions: LabelValuePair[] = Array.isArray(
    data.additionalConditions,
  )
    ? data.additionalConditions.filter(isLabelValuePair)
    : [];

  const additionalRequests: LabelValuePair[] = Array.isArray(
    data.additionalRequests,
  )
    ? data.additionalRequests.filter(isLabelValuePair)
    : [];

  // Filter based on category type
  const displayItems =
    categoryType === "product" ? additionalConditions : additionalRequests;

  const sectionTitle =
    categoryType === "product" ? "추가 조건" : "추가 요청사항";
  const isEmpty = displayItems.length === 0;

  return (
    <div className="bg-white rounded-[20px] p-6 sm:p-8">
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="w-10 h-10 rounded-[12px] bg-primary/5 flex items-center justify-center text-primary shrink-0">
          <CheckSquare className="w-5 h-5" />
        </div>

        {/* Title + Chips inline */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2.5">
            <h3 className="text-lg font-semibold text-gray-900 shrink-0">
              {sectionTitle}
            </h3>
            {!isEmpty &&
              displayItems.map((item: LabelValuePair, index: number) => (
                <div
                  key={index}
                  className="px-3 py-1 bg-gray-50 rounded-full text-sm font-semibold text-gray-600"
                >
                  {typeof item === "string" ? item : item.label || item.value}
                </div>
              ))}
            {isEmpty && (
              <p className="text-sm text-gray-400 font-medium">없음</p>
            )}
          </div>
        </div>

        {/* Edit button - optional */}
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className={cn(
              "px-4 py-1.5",
              "text-gray-400",
              "rounded-[10px]",
              "hover:bg-gray-50 hover:text-primary",
              "transition-all",
              "text-sm font-semibold shrink-0",
            )}
          >
            수정
          </button>
        )}
      </div>
    </div>
  );
};
